import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Sparkles, Building2, User, Mail, Phone, MapPin, 
  CreditCard, Check, ArrowRight, ArrowLeft, Upload,
  Globe, Briefcase, Users, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
  { value: 'spa', label: 'Spa & Wellness Center' },
  { value: 'salon', label: 'Beauty Salon' },
  { value: 'resort_spa', label: 'Resort/Hotel Spa' },
  { value: 'ayurveda_center', label: 'Ayurveda Center' },
  { value: 'wellness_center', label: 'Wellness & Fitness Center' },
  { value: 'medical_spa', label: 'Medical Spa' },
  { value: 'other', label: 'Other' }
];

const COMPANY_SIZES = [
  { value: 'small', label: '1-5 locations' },
  { value: 'medium', label: '6-20 locations' },
  { value: 'large', label: '21-50 locations' },
  { value: 'enterprise', label: '50+ locations' }
];

const HOW_DID_YOU_HEAR = [
  { value: 'google', label: 'Google Search' },
  { value: 'referral', label: 'Referral from a friend' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'advertisement', label: 'Online Advertisement' },
  { value: 'event', label: 'Industry Event/Conference' },
  { value: 'other', label: 'Other' }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Plan limits mapping
const PLAN_LIMITS = {
  'starter_monthly': { locations: 3, therapists: 10 },
  'starter_annual': { locations: 3, therapists: 10 },
  'professional_monthly': { locations: 10, therapists: 50 },
  'professional_annual': { locations: 10, therapists: 50 },
  'enterprise_monthly': { locations: -1, therapists: -1 },
  'enterprise_annual': { locations: -1, therapists: -1 }
};

// Check if a plan fits the requirements
const checkPlanFitsRequirements = (planId, locations, staff) => {
  const limits = PLAN_LIMITS[planId];
  if (!limits) return false;
  const locationsOk = limits.locations === -1 || locations <= limits.locations;
  const staffOk = limits.therapists === -1 || staff <= limits.therapists;
  return locationsOk && staffOk;
};

// Get recommended plan based on locations and staff
const getRecommendedPlan = (locations, staff, plans) => {
  const planPriority = ['starter_monthly', 'professional_monthly', 'enterprise_monthly'];
  for (const planId of planPriority) {
    if (checkPlanFitsRequirements(planId, locations, staff)) {
      return plans.find(p => p.id === planId);
    }
  }
  return plans.find(p => p.id === 'enterprise_monthly');
};

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {Array.from({ length: totalSteps }, (_, i) => (
      <React.Fragment key={i}>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
          i + 1 <= currentStep 
            ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' 
            : 'bg-slate-700 text-slate-400'
        }`}>
          {i + 1 <= currentStep - 1 ? <Check className="w-5 h-5" /> : i + 1}
        </div>
        {i < totalSteps - 1 && (
          <div className={`w-12 h-1 rounded ${
            i + 1 < currentStep ? 'bg-emerald-500' : 'bg-slate-700'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

export default function TenantOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  
  // Platform status
  const [platformStatus, setPlatformStatus] = useState({ platform_mode: 'test' });
  
  // OTP verification states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  
  // Duplicate check states
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  
  // Config from backend
  const [config, setConfig] = useState({ trial_days: 7, business_types: [] });
  
  const [formData, setFormData] = useState({
    // Step 1: Business Info
    company_name: '',
    subdomain: '',
    business_type: 'spa',
    company_size: 'small',
    
    // Step 2: Admin Info
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    admin_password_confirm: '',
    admin_designation: 'Owner',
    
    // Step 3: Business Details (now mandatory)
    gst_number: '',
    pan_number: '',
    headquarters_address: '',
    city: '',
    state: '',
    pincode: '',
    num_spa_locations: '',
    num_staff_to_manage: '',
    
    // Step 4: Plan & Branding
    selected_plan: searchParams.get('plan') || 'starter_monthly',
    logo_url: '',
    primary_color: '#8B7355',
    secondary_color: '#D4AF37',
    how_did_you_hear: '',
    referral_code: ''
  });
  
  // Form validation errors state
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchPlans();
    fetchConfig();
    fetchPlatformStatus();
  }, []);
  
  const fetchPlatformStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/platform-status`);
      if (res.ok) {
        const data = await res.json();
        setPlatformStatus(data);
        if (data.trial_days) {
          setConfig(prev => ({ ...prev, trial_days: data.trial_days }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch platform status');
    }
  };
  
  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch config');
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenant/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const checkSubdomainAvailability = async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }
    
    setCheckingSubdomain(true);
    try {
      const res = await fetch(`${API_URL}/api/tenant/check-subdomain/${subdomain}`);
      const data = await res.json();
      setSubdomainAvailable(data.available);
    } catch (error) {
      setSubdomainAvailable(null);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  // Check for duplicate GST/Phone
  const checkDuplicate = async () => {
    if (!formData.admin_phone || formData.admin_phone.length < 10) {
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/check-duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: formData.admin_phone,
          gst_number: formData.gst_number || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setDuplicateCheck(data);
        if (data.is_duplicate) {
          toast.warning(data.message);
        }
      }
    } catch (error) {
      console.error('Duplicate check failed');
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Send OTP for email verification
  const sendOtp = async () => {
    if (!formData.admin_email.includes('@')) {
      toast.error('Please enter a valid email first');
      return;
    }
    
    setSendingOtp(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.admin_email,
          admin_name: formData.admin_name,
          company_name: formData.company_name
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setOtpSent(true);
        toast.success('OTP sent to your email!');
      } else {
        toast.error(data.detail || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP
  const verifyOtp = async () => {
    if (!otpInput || otpInput.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setVerifyingOtp(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.admin_email,
          otp: otpInput
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.verified) {
        setOtpVerified(true);
        setOtpToken(data.token);
        toast.success('Email verified successfully!');
      } else {
        toast.error(data.detail || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('OTP verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'subdomain') {
      // Auto-generate subdomain from company name if empty
      const cleanValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, subdomain: cleanValue }));
      
      // Debounce subdomain check
      clearTimeout(window.subdomainTimeout);
      window.subdomainTimeout = setTimeout(() => {
        checkSubdomainAvailability(cleanValue);
      }, 500);
    }
    
    if (field === 'company_name' && !formData.subdomain) {
      const suggestedSubdomain = value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20);
      setFormData(prev => ({ ...prev, subdomain: suggestedSubdomain }));
      checkSubdomainAvailability(suggestedSubdomain);
    }
  };

  const validateStep = (stepNum) => {
    const errors = {};
    
    switch (stepNum) {
      case 1:
        if (!formData.company_name.trim()) {
          toast.error('Please enter your company name');
          return false;
        }
        if (!formData.subdomain || formData.subdomain.length < 3) {
          toast.error('Subdomain must be at least 3 characters');
          return false;
        }
        if (subdomainAvailable === false) {
          toast.error('This subdomain is not available');
          return false;
        }
        return true;
        
      case 2:
        if (!formData.admin_name.trim()) {
          toast.error('Please enter admin name');
          return false;
        }
        if (!formData.admin_email.includes('@')) {
          toast.error('Please enter a valid email');
          return false;
        }
        if (!formData.admin_phone || formData.admin_phone.length < 10) {
          toast.error('Please enter a valid phone number');
          return false;
        }
        if (!otpVerified) {
          toast.error('Please verify your email with OTP');
          return false;
        }
        if (!formData.admin_password || formData.admin_password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return false;
        }
        if (formData.admin_password !== formData.admin_password_confirm) {
          toast.error('Passwords do not match');
          return false;
        }
        // Check duplicate after phone is entered
        if (!duplicateCheck) {
          checkDuplicate();
        }
        return true;
        
      case 3:
        // Validate mandatory fields
        if (!formData.city.trim()) {
          errors.city = 'City is required';
        }
        if (!formData.headquarters_address.trim()) {
          errors.headquarters_address = 'Address is required';
        }
        if (!formData.pincode || formData.pincode.length < 5) {
          errors.pincode = 'Valid pincode is required';
        }
        // Either GST or PAN is required
        if (!formData.gst_number.trim() && !formData.pan_number.trim()) {
          errors.gst_pan = 'Either GST Number or PAN Number is required';
        }
        // Number of Locations is required
        const numLocations = parseInt(formData.num_spa_locations);
        if (!formData.num_spa_locations || isNaN(numLocations) || numLocations < 1) {
          errors.num_spa_locations = 'Number of Locations is required (minimum 1)';
        }
        // Number of Staff is required
        const numStaff = parseInt(formData.num_staff_to_manage);
        if (!formData.num_staff_to_manage || isNaN(numStaff) || numStaff < 1) {
          errors.num_staff_to_manage = 'Number of Staff to manage is required (minimum 1)';
        }
        
        setFormErrors(errors);
        
        if (Object.keys(errors).length > 0) {
          const firstError = Object.values(errors)[0];
          toast.error(firstError);
          return false;
        }
        return true;
        
      case 4:
        if (!formData.selected_plan) {
          toast.error('Please select a plan');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    // Verify OTP was completed
    if (!otpVerified || !otpToken) {
      toast.error('Please verify your email first');
      setStep(2);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          num_spa_locations: parseInt(formData.num_spa_locations) || 1,
          num_staff_to_manage: parseInt(formData.num_staff_to_manage) || 1,
          otp_token: otpToken
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Check if existing user being redirected to payment
        if (data.redirect_to_payment) {
          toast.info(data.message || 'Please complete your subscription to activate.');
          localStorage.setItem('pending_registration', JSON.stringify({
            ...formData,
            company_id: data.company_id
          }));
          navigate(`/onboarding/payment?company=${data.company_id}&plan=${data.plan || formData.selected_plan}&locations=${formData.num_spa_locations}&staff=${formData.num_staff_to_manage}`);
          return;
        }
        
        // Check if returning customer (no free trial)
        if (data.is_returning_customer) {
          toast.info('Previous registration detected. Please subscribe to activate your account.');
          localStorage.setItem('pending_registration', JSON.stringify(data));
          navigate(`/onboarding/payment?company=${data.company_id}&plan=${formData.selected_plan}&locations=${formData.num_spa_locations}&staff=${formData.num_staff_to_manage}&returning=true`);
        } else {
          toast.success(`Registration successful! Your ${config.trial_days}-day free trial has started.`);
          localStorage.setItem('pending_registration', JSON.stringify(data));
          // For new customers with free trial, they can optionally pay now
          navigate(`/onboarding/success?company=${data.company_id}&trial=true`);
        }
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlanData = plans.find(p => p.id === formData.selected_plan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">WellNest Technologies</span>
          </div>
          
          <Button variant="ghost" onClick={() => navigate('/login')} 
            className="text-slate-300">
            Already have an account?
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Test Mode Info Banner - Shows test card info, doesn't block */}
        {platformStatus.platform_mode === 'test' && platformStatus.is_test_mode && (
          <div className="mb-8 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-amber-400 font-semibold">Test Environment</h3>
                <p className="text-slate-300 text-sm">
                  Platform is in test mode. Use test card: <span className="font-mono bg-slate-700 px-2 py-0.5 rounded">4111 1111 1111 1111</span> (any expiry, any CVV)
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Create Your WellNest Technologies Account
          </h1>
          <p className="text-slate-400">
            Get started with your {config.trial_days}-day free trial
          </p>
          {duplicateCheck?.is_duplicate && (
            <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
              Note: We found a previous registration with your phone/GST. Free trial is only for new customers.
            </div>
          )}
        </div>

        <StepIndicator currentStep={step} totalSteps={4} />

        {/* Step 1: Business Info */}
        {step === 1 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-400" />
                Business Information
              </CardTitle>
              <CardDescription className="text-slate-400">
                Tell us about your business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-slate-300">Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="e.g., Your Wellness Company"
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Choose Your Subdomain *</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={formData.subdomain}
                    onChange={(e) => handleInputChange('subdomain', e.target.value)}
                    placeholder="yourcompany"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  <span className="text-slate-400 whitespace-nowrap">.wellnesttechnologies.com</span>
                </div>
                {checkingSubdomain && (
                  <p className="text-sm text-slate-400">Checking availability...</p>
                )}
                {subdomainAvailable === true && (
                  <p className="text-sm text-emerald-400 flex items-center gap-1">
                    <Check className="w-4 h-4" /> Available
                  </p>
                )}
                {subdomainAvailable === false && (
                  <p className="text-sm text-red-400">
                    This subdomain is not available
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Business Type</Label>
                  <select
                    value={formData.business_type}
                    onChange={(e) => handleInputChange('business_type', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                  >
                    {(config.business_types?.length > 0 ? config.business_types : BUSINESS_TYPES).map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Company Size</Label>
                  <select
                    value={formData.company_size}
                    onChange={(e) => handleInputChange('company_size', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                  >
                    {COMPANY_SIZES.map(size => (
                      <option key={size.value} value={size.value}>{size.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleNext}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600">
                  Continue <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Admin Info */}
        {step === 2 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-400" />
                Admin Account
              </CardTitle>
              <CardDescription className="text-slate-400">
                Set up your administrator account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Full Name *</Label>
                  <Input
                    value={formData.admin_name}
                    onChange={(e) => handleInputChange('admin_name', e.target.value)}
                    placeholder="Your full name"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Designation</Label>
                  <Input
                    value={formData.admin_designation}
                    onChange={(e) => handleInputChange('admin_designation', e.target.value)}
                    placeholder="e.g., Owner, Manager"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Address *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => {
                        handleInputChange('admin_email', e.target.value);
                        setOtpSent(false);
                        setOtpVerified(false);
                      }}
                      placeholder="admin@yourcompany.com"
                      className="bg-slate-700/50 border-slate-600 text-white flex-1"
                      disabled={otpVerified}
                    />
                    {!otpVerified && (
                      <Button 
                        type="button"
                        onClick={sendOtp}
                        disabled={sendingOtp || !formData.admin_email.includes('@') || !formData.admin_name}
                        className="bg-teal-600 hover:bg-teal-700 whitespace-nowrap"
                      >
                        {sendingOtp ? 'Sending...' : otpSent ? 'Resend OTP' : 'Send OTP'}
                      </Button>
                    )}
                  </div>
                  {otpVerified && (
                    <p className="text-sm text-emerald-400 flex items-center gap-1">
                      <Check className="w-4 h-4" /> Email verified
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone Number *</Label>
                  <Input
                    value={formData.admin_phone}
                    onChange={(e) => {
                      handleInputChange('admin_phone', e.target.value);
                      setDuplicateCheck(null);
                    }}
                    onBlur={() => formData.admin_phone?.length >= 10 && checkDuplicate()}
                    placeholder="+91 98765 43210"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                  {checkingDuplicate && (
                    <p className="text-sm text-slate-400">Checking...</p>
                  )}
                </div>
              </div>
              
              {/* OTP Verification Section */}
              {otpSent && !otpVerified && (
                <div className="p-4 bg-teal-500/10 border border-teal-500/30 rounded-lg space-y-3">
                  <Label className="text-teal-400">Enter the 6-digit OTP sent to your email</Label>
                  <div className="flex gap-2">
                    <Input
                      value={otpInput}
                      onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="bg-slate-700/50 border-slate-600 text-white text-center text-2xl tracking-widest max-w-[200px]"
                      maxLength={6}
                    />
                    <Button 
                      onClick={verifyOtp}
                      disabled={verifyingOtp || otpInput.length !== 6}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {verifyingOtp ? 'Verifying...' : 'Verify'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400">
                    Didn't receive the OTP? Check your spam folder or click "Resend OTP"
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Password *</Label>
                  <Input
                    type="password"
                    value={formData.admin_password}
                    onChange={(e) => handleInputChange('admin_password', e.target.value)}
                    placeholder="Minimum 6 characters"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Confirm Password *</Label>
                  <Input
                    type="password"
                    value={formData.admin_password_confirm}
                    onChange={(e) => handleInputChange('admin_password_confirm', e.target.value)}
                    placeholder="Confirm your password"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleBack}
                  className="border-slate-600 text-slate-300">
                  <ArrowLeft className="mr-2 w-4 h-4" /> Back
                </Button>
                <Button onClick={handleNext}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600">
                  Continue <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Business Details */}
        {step === 3 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-emerald-400" />
                Business Details
              </CardTitle>
              <CardDescription className="text-slate-400">
                Required information for your business registration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* GST / PAN - At least one required */}
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm mb-3 font-medium">
                  Either GST Number or PAN Number is required *
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">GST Number</Label>
                    <Input
                      value={formData.gst_number}
                      onChange={(e) => {
                        handleInputChange('gst_number', e.target.value.toUpperCase());
                        setFormErrors(prev => ({...prev, gst_pan: null}));
                      }}
                      placeholder="22AAAAA0000A1Z5"
                      className={`bg-slate-700/50 border-slate-600 text-white ${formErrors.gst_pan ? 'border-red-500' : ''}`}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">PAN Number</Label>
                    <Input
                      value={formData.pan_number}
                      onChange={(e) => {
                        handleInputChange('pan_number', e.target.value.toUpperCase());
                        setFormErrors(prev => ({...prev, gst_pan: null}));
                      }}
                      placeholder="AAAAA0000A"
                      className={`bg-slate-700/50 border-slate-600 text-white ${formErrors.gst_pan ? 'border-red-500' : ''}`}
                    />
                  </div>
                </div>
                {formErrors.gst_pan && (
                  <p className="text-red-400 text-sm mt-2">{formErrors.gst_pan}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Headquarters Address *</Label>
                <Textarea
                  value={formData.headquarters_address}
                  onChange={(e) => {
                    handleInputChange('headquarters_address', e.target.value);
                    setFormErrors(prev => ({...prev, headquarters_address: null}));
                  }}
                  placeholder="Street address, Building name"
                  className={`bg-slate-700/50 border-slate-600 text-white ${formErrors.headquarters_address ? 'border-red-500' : ''}`}
                  rows={2}
                />
                {formErrors.headquarters_address && (
                  <p className="text-red-400 text-sm">{formErrors.headquarters_address}</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => {
                      handleInputChange('city', e.target.value);
                      setFormErrors(prev => ({...prev, city: null}));
                    }}
                    placeholder="Mumbai"
                    className={`bg-slate-700/50 border-slate-600 text-white ${formErrors.city ? 'border-red-500' : ''}`}
                  />
                  {formErrors.city && (
                    <p className="text-red-400 text-sm">{formErrors.city}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="Maharashtra"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Pincode *</Label>
                  <Input
                    value={formData.pincode}
                    onChange={(e) => {
                      handleInputChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6));
                      setFormErrors(prev => ({...prev, pincode: null}));
                    }}
                    placeholder="400001"
                    className={`bg-slate-700/50 border-slate-600 text-white ${formErrors.pincode ? 'border-red-500' : ''}`}
                  />
                  {formErrors.pincode && (
                    <p className="text-red-400 text-sm">{formErrors.pincode}</p>
                  )}
                </div>
              </div>
              
              {/* Operational Details - Required for package recommendation */}
              <div className="border-t border-slate-700 pt-6">
                <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-400" />
                  Operational Details (Required for package recommendation)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Number of Locations *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.num_spa_locations}
                      onChange={(e) => {
                        handleInputChange('num_spa_locations', e.target.value);
                        setFormErrors(prev => ({...prev, num_spa_locations: null}));
                      }}
                      placeholder="e.g., 6"
                      className={`bg-slate-700/50 border-slate-600 text-white ${formErrors.num_spa_locations ? 'border-red-500' : ''}`}
                    />
                    {formErrors.num_spa_locations && (
                      <p className="text-red-400 text-sm">{formErrors.num_spa_locations}</p>
                    )}
                    <p className="text-slate-500 text-xs">Total number of wellness locations you manage</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">Number of Staff to Manage *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.num_staff_to_manage}
                      onChange={(e) => {
                        handleInputChange('num_staff_to_manage', e.target.value);
                        setFormErrors(prev => ({...prev, num_staff_to_manage: null}));
                      }}
                      placeholder="e.g., 20"
                      className={`bg-slate-700/50 border-slate-600 text-white ${formErrors.num_staff_to_manage ? 'border-red-500' : ''}`}
                    />
                    {formErrors.num_staff_to_manage && (
                      <p className="text-red-400 text-sm">{formErrors.num_staff_to_manage}</p>
                    )}
                    <p className="text-slate-500 text-xs">Total therapists and staff members across all locations</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleBack}
                  className="border-slate-600 text-slate-300">
                  <ArrowLeft className="mr-2 w-4 h-4" /> Back
                </Button>
                <Button onClick={handleNext}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600">
                  Continue <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Plan Selection & Branding */}
        {step === 4 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Choose Your Plan
              </CardTitle>
              <CardDescription className="text-slate-400">
                Select a plan and customize your branding
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Recommendation Banner */}
              {(() => {
                const numLocations = parseInt(formData.num_spa_locations) || 1;
                const numStaff = parseInt(formData.num_staff_to_manage) || 1;
                const recommended = getRecommendedPlan(numLocations, numStaff, plans);
                
                // Auto-select recommended plan if current selection doesn't fit
                if (recommended && !checkPlanFitsRequirements(formData.selected_plan, numLocations, numStaff)) {
                  // Only auto-select once
                  if (formData.selected_plan !== recommended.id) {
                    setTimeout(() => handleInputChange('selected_plan', recommended.id), 0);
                  }
                }
                
                return recommended && (numLocations > 1 || numStaff > 1) && (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl mb-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-emerald-400 font-semibold mb-1">Recommended Plan</h3>
                        <p className="text-slate-300 text-sm">
                          Based on your requirements ({numLocations} location{numLocations > 1 ? 's' : ''} and {numStaff} staff member{numStaff > 1 ? 's' : ''}), 
                          we recommend the <span className="text-white font-semibold">{recommended.name}</span> plan.
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Plan Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.filter(p => p.billing_cycle === 'monthly').map((plan) => {
                  const numLocations = parseInt(formData.num_spa_locations) || 1;
                  const numStaff = parseInt(formData.num_staff_to_manage) || 1;
                  const limits = PLAN_LIMITS[plan.id] || { locations: 0, therapists: 0 };
                  const fitsRequirements = checkPlanFitsRequirements(plan.id, numLocations, numStaff);
                  const isRecommended = getRecommendedPlan(numLocations, numStaff, plans)?.id === plan.id;
                  
                  return (
                    <div 
                      key={plan.id}
                      onClick={() => {
                        if (fitsRequirements) {
                          handleInputChange('selected_plan', plan.id);
                        } else {
                          toast.error(`This plan doesn't support ${numLocations} locations and ${numStaff} staff. Please choose a higher plan.`);
                        }
                      }}
                      className={`relative p-4 rounded-xl border-2 transition-all ${
                        !fitsRequirements 
                          ? 'border-slate-700 bg-slate-800/30 opacity-60 cursor-not-allowed'
                          : formData.selected_plan === plan.id
                            ? 'border-emerald-500 bg-emerald-500/10 cursor-pointer'
                            : 'border-slate-600 hover:border-slate-500 cursor-pointer'
                      }`}
                    >
                      {isRecommended && fitsRequirements && (
                        <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-white">
                          Recommended
                        </Badge>
                      )}
                      {!fitsRequirements && (
                        <Badge className="absolute -top-2 -right-2 bg-red-500/80 text-white text-xs">
                          Insufficient
                        </Badge>
                      )}
                      <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                      <p className="text-2xl font-bold text-white mt-2">
                        {formatCurrency(plan.amount)}
                        <span className="text-sm text-slate-400 font-normal">/mo</span>
                      </p>
                      
                      {/* Plan Limits */}
                      <div className="mt-3 space-y-1 text-sm border-t border-slate-700 pt-3">
                        <div className={`flex items-center gap-2 ${numLocations > limits.locations && limits.locations !== -1 ? 'text-red-400' : 'text-slate-300'}`}>
                          <MapPin className="w-4 h-4" />
                          {limits.locations === -1 ? 'Unlimited' : `Up to ${limits.locations}`} locations
                        </div>
                        <div className={`flex items-center gap-2 ${numStaff > limits.therapists && limits.therapists !== -1 ? 'text-red-400' : 'text-slate-300'}`}>
                          <Users className="w-4 h-4" />
                          {limits.therapists === -1 ? 'Unlimited' : `Up to ${limits.therapists}`} staff
                        </div>
                      </div>
                      
                      <ul className="mt-4 space-y-2">
                        {plan.features.slice(0, 2).map((feature, i) => (
                          <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
              
              {/* Branding */}
              <div className="border-t border-slate-700 pt-6">
                <h4 className="text-white font-semibold mb-4">Customize Your Branding (Optional)</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Logo URL</Label>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => handleInputChange('logo_url', e.target.value)}
                      placeholder="https://example.com/logo.png"
                      className="bg-slate-700/50 border-slate-600 text-white"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Primary Color</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={formData.primary_color}
                          onChange={(e) => handleInputChange('primary_color', e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={formData.primary_color}
                          onChange={(e) => handleInputChange('primary_color', e.target.value)}
                          className="bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-300">Accent Color</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={formData.secondary_color}
                          onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={formData.secondary_color}
                          onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                          className="bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Marketing Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">How did you hear about us?</Label>
                  <select
                    value={formData.how_did_you_hear}
                    onChange={(e) => handleInputChange('how_did_you_hear', e.target.value)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-md px-3 py-2 text-white"
                  >
                    <option value="">Select an option</option>
                    {HOW_DID_YOU_HEAR.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Referral Code (Optional)</Label>
                  <Input
                    value={formData.referral_code}
                    onChange={(e) => handleInputChange('referral_code', e.target.value)}
                    placeholder="Enter referral code"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-slate-700/30 rounded-xl p-4 border border-slate-600">
                <h4 className="text-white font-semibold mb-3">Order Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-slate-300">
                    <span>Plan</span>
                    <span>{selectedPlanData?.name || 'Starter'}</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Billing</span>
                    <span>Monthly</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Trial Period</span>
                    <span className="text-emerald-400">{config.trial_days} days free</span>
                  </div>
                  <div className="border-t border-slate-600 pt-2 mt-2">
                    <div className="flex justify-between text-white font-semibold">
                      <span>Due Today</span>
                      <span className="text-emerald-400">₹0</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      You'll be charged {formatCurrency(selectedPlanData?.amount || 999)} after your trial ends
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleBack}
                  className="border-slate-600 text-slate-300">
                  <ArrowLeft className="mr-2 w-4 h-4" /> Back
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={loading}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600"
                >
                  {loading 
                    ? 'Creating Account...' 
                    : platformStatus.is_test_mode 
                      ? 'Start Free Trial (Test Mode)' 
                      : 'Start Free Trial'} 
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
